import React, { useEffect, useState, useCallback } from 'react';
import DashboardCard from '@/app/fuse-layouts/layout1/components/Dashboard/DashboardCard';
import { CircularProgress, Tooltip, Avatar } from '@material-ui/core';
import 'stream-chat-react/dist/css/v2/index.css';
import { useSelector } from 'react-redux';
import { Chat, ChannelList } from 'stream-chat-react';
import { formatDistance } from 'date-fns';
import { ChatBubbleOvalLeftIcon, UserIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';

const LoadingChannels = () => (
	<div className="flex justify-center items-center h-full min-h-[300px]">
		<CircularProgress size={35} />
	</div>
);

const CustomChannelList = ({ loading, error, children }) => {
	const user = useSelector(({ auth }) => auth.user);
	const isSuperSchoolAdmin = user?.role[0] === 'super_school_admin';
	const [customLoading, setCustomLoading] = useState(false);

	// custom loading state only for super_school_admin
	useEffect(() => {
		if (isSuperSchoolAdmin) {
			setCustomLoading(true);
			const timer = setTimeout(() => {
				setCustomLoading(false);
			}, 2500);

			return () => clearTimeout(timer);
		}
	}, [isSuperSchoolAdmin, user?.school?.id]);

	if (customLoading || loading) {
		return <LoadingChannels />;
	}

	if (error) {
		return (
			<div className="justify-center items-center h-full min-h-[300px] flex-col gap-4">
				<div>Error loading channels</div>
				<button
					className="px-4 py-2 bg-primary text-white rounded"
					onClick={() => window.location.reload()}
					type="button"
				>
					Reload page
				</button>
			</div>
		);
	}

	return (
		<div className="str-chat__channel-list-messenger str-chat__channel-list-messenger-react">
			<div
				className="str-chat__channel-list-messenger__main str-chat__channel-list-messenger-react__main"
				role="listbox"
			>
				{children}
			</div>
		</div>
	);
};

const MessageListing = () => {
	const client = useSelector(({ getstream }) => getstream.getstream.client);
	const user = useSelector(({ auth }) => auth.user);
	const [isLoading, setIsLoading] = useState(true);
	const isSuperSchoolAdmin = user?.role[0] === 'super_school_admin';
	const [increment, setIncrement] = useState(0);
	const [debounceTimeout, setDebounceTimeout] = useState(null);
	const navigate = useNavigate();
	useEffect(() => {
		if (client) {
			setIsLoading(false);
		}
	}, [client]);

	const debouncedIncrement = useCallback(() => {
		if (debounceTimeout) {
			clearTimeout(debounceTimeout);
		}
		const timeout = setTimeout(() => {
			setIncrement((prev) => prev + 1);
		}, 500);
		setDebounceTimeout(timeout);
	}, [debounceTimeout]);

	useEffect(() => {
		return () => {
			if (debounceTimeout) {
				clearTimeout(debounceTimeout);
			}
		};
	}, [debounceTimeout]);

	const filters = {
		members: { $in: [client?.userID] },
		type: { $in: ['admin-messaging'] },
		last_message_at: { $exists: true },
		school_id: { $eq: isSuperSchoolAdmin ? user?.school?.id : user?.data?.school?.id },
	};
	const sort = { last_message_at: -1 };
	const options = {
		limit: 5,
		state: true,
		watch: true,
		message_limit: 1,
	};

	// when user is added to a new channel we need to increment the key to refetch the channels
	// mainly for when a super_admin switches schools
	const handleNewMessageOrChannel = () => {
		debouncedIncrement();
	};

	const handleMessageFromUnfilteredChannel = (_, e) => {
		/* before the first message in a channel is sent, the 
		current user will not be in its members list
		so we need to refetch the channels for the new message to show up */

		if (e?.channel_type === 'admin-messaging') {
			debouncedIncrement();
		}
	};

	return (
		<DashboardCard onViewMore={() => navigate('/messaging')} showViewMore={true} title="Messaging">
			<div
				className="flex relative flex-col gap-20 dashboard-message-listing"
				style={{ background: '#fff', minHeight: '422px' }}
			>
				{isLoading ? (
					<div className="flex justify-center absolute inset-0 items-center mt-16">
						<CircularProgress size={35} />
					</div>
				) : null}

				{!isLoading && client ? (
					<Chat client={client} key={client.userID}>
						<ChannelList
							List={CustomChannelList}
							Preview={CustomChannelPreview}
							allowNewMessagesFromUnfilteredChannels={false}
							filters={filters}
							key={increment}
							onAddedToChannel={handleNewMessageOrChannel}
							onMessageNew={handleMessageFromUnfilteredChannel}
							options={options}
							sort={sort}
						/>
					</Chat>
				) : null}
			</div>
		</DashboardCard>
	);
};

const CustomChannelPreview = ({ channel }) => {
	const studentFirstName = channel.data?.student_first_name || 'Unnamed';
	const studentLastName = channel.data?.student_last_name || 'Student';
	const studentName = channel.data?.student_name || `${studentFirstName} ${studentLastName}`;
	const fallbackInitial = studentName.charAt(0).toUpperCase();
	const lastMessage = channel.state.messages[channel.state.messages.length - 1];
	const navigate = useNavigate();

	const timeAgo = (date) => (date ? formatDistance(new Date(date), new Date(), { addSuffix: true }) : '');

	const fullDate = (date) => {
		if (!date) return '';
		return new Date(date).toLocaleString();
	};

	const titleCase = (str) => {
		// Convert the first letter of each word to uppercase
		if (!str) return '';
		return str.replace(/\b\w/g, (char) => char.toUpperCase());
	};

	const handleSelectChannel = () => {
		const type = channel.data?.type === 'teacher-messaging' ? 'teacher' : 'admin';
		navigate(`/messaging/${type}/chat/${channel.id}`);
	};

	return (
		<div className="border-b relative" data-channel={channel.id}>
			<button aria-label="Select Channel" className="flex w-full py-[12px]" onClick={handleSelectChannel} type="button">
				{/* md+ avatar on left */}
				<div
					className="!hidden md:!block str-chat__avatar str-chat__message-sender-avatar str-chat__avatar--channel-preview str-chat__avatar--one-letter relative"
					title={studentName}
				>
					{channel.state.unreadCount > 0 ? (
						<Avatar className="bg-red-500 text-[10px] w-[20px] h-[20px] flex items-center justify-center text-white font-bold absolute -top-8 right-4 z-30">
							{channel.state.unreadCount}
						</Avatar>
					) : null}
					{channel.data?.student_image ? (
						<Avatar alt={studentName} src={channel.data.student_image} />
					) : (
						<div className="str-chat__avatar-fallback">{fallbackInitial}</div>
					)}
				</div>

				<div className="flex flex-col justify-between items-start flex-1 min-w-0  text-left hover:bg-gray-50 transition-colors duration-150">
					{/* header row */}
					<div className="flex flex-row justify-between items-center w-full">
						<div className="flex items-center gap-4">
							<div
								className="md:!hidden str-chat__avatar str-chat__message-sender-avatar str-chat__avatar--channel-preview str-chat__avatar--one-letter relative flex-shrink-0"
								title={studentName}
							>
								{channel.state.unreadCount > 0 ? (
									<Avatar className="absolute z-30 -top-4 -right-4 bg-red-500 text-[10px] w-[20px] h-[20px] flex items-center justify-center text-white font-bold">
										{channel.state.unreadCount}
									</Avatar>
								) : null}

								{channel.data?.student_image ? (
									<Avatar alt={studentName} src={channel.data.student_image} />
								) : (
									<div className="str-chat__avatar-fallback">{fallbackInitial}</div>
								)}
							</div>
							<div className="font-bold text-12 truncate">{studentName}</div>
						</div>
						{lastMessage ? (
							<div className="ml-2">
								<Tooltip title={fullDate(lastMessage.created_at)}>
									<div className="text-left text-10 text-gray-700 whitespace-normal break-normal">
										{timeAgo(lastMessage.created_at)}
									</div>
								</Tooltip>
							</div>
						) : null}
					</div>

					{/* message preview */}
					<div className="flex flex-col sm:flex-row items-start sm:items-center justify-between flex-1 min-w-0 gap-4">
						{lastMessage ? (
							<div className="flex-1 min-w-0">
								<Tooltip title={lastMessage.user.name}>
									<div className="truncate font-semibold flex items-center gap-4">
										<UserIcon className="size-[20px] min-w-[16px] text-gray-500" />
										<span className="text-gray-800 text-12">{lastMessage.user.name}</span>
										{lastMessage.user.user_type ? (
											<div className="font-bold text-10 bg-grey-300 rounded-full px-4 py-1">
												{titleCase(lastMessage.user.user_type)}
											</div>
										) : null}
									</div>
								</Tooltip>
								<div className="flex items-center gap-4 flex-1 min-w-0 overflow-hidden">
									<ChatBubbleOvalLeftIcon className="size-[20px] min-w-[16px] text-gray-500 scale-x-[-1]" />
									<Tooltip title={lastMessage.text}>
										<span className="truncate block text-gray-900 text-12 max-w-[250px] sm:max-w-[300px] min-[650px]:max-w-[400px] md:max-w-[300px] lg:max-w-[200px] xl:max-w-[500px]">
											{lastMessage.attachments.length ? (
												<span className="italic">shared an attachment</span>
											) : (
												lastMessage.text
											)}
										</span>
									</Tooltip>
								</div>
							</div>
						) : (
							<p className="text-gray-500">No messages yet</p>
						)}
					</div>
				</div>
			</button>
		</div>
	);
};

export default MessageListing;
